import { memo, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import styled from "styled-components";
import Text from "@/components/Text";
import Theme from "@/utils/theme";
import Spacer from "@/components/Spacer";
import {
  Container as ProgressTrackBase,
  CurrentPointsContainer,
} from "@/components/PointsSlider/styles";
import InfoIcon from "@/ui/icons/InfoIcon";
import CircleCheck from "@/ui/icons/CircleCheck";
import { useInscriptionStatus } from "@/hooks/useInscriptionStatus";
import { usePdcExternalCoursesQuery } from "@/hooks/usePdcExternalCoursesQuery";
import { useStages } from "@/context/StagesContext";
import { isPdcExternalCourseListEnabled } from "@/utils/pdc-external-course-list";
import { derivePdcCourseListStats } from "@/services/pdcExternal/courses";

/** Same track as PointsSlider, with gray border and rounded corners on the outer track only. */
const ProgressTrack = styled(ProgressTrackBase)`
  border: 1px solid ${({ theme }) => theme.colors.neutral.mid};
  border-radius: 1rem;
  overflow: hidden;
`;

/** Fill segment with centered % label (cadastro progress bar). */
const CadastroProgressFill = styled(CurrentPointsContainer)`
  justify-content: center;
  align-items: center;
  border-radius: 1rem;
`;

type MeuProgressoSectionProps = {
  isValidStage: boolean;
};

/**
 * Bar = (desafios com envio + cursos concluídos) / total de itens.
 * Rodapé "concluído" = 100% do cadastro (desafios enviados + cursos 100% finalizados).
 */
const MeuProgressoSection = ({ isValidStage }: MeuProgressoSectionProps) => {
  const { data: inscription, isLoading } = useInscriptionStatus();
  const { isUserOutOfReality } = useStages();
  const usePdcList = isPdcExternalCourseListEnabled();
  const pdcQuery = usePdcExternalCoursesQuery({ enabled: usePdcList });

  const { ratio, percentRounded, isRegistrationComplete } = useMemo(() => {
    if (!inscription) {
      return { ratio: 0, percentRounded: 0, isRegistrationComplete: false };
    }
    const pdcStats =
      usePdcList && pdcQuery.data !== undefined
        ? derivePdcCourseListStats(pdcQuery.data)
        : undefined;
    const totalCoursesForBar = pdcStats
      ? Math.max(pdcStats.totalCoursesCount, inscription.totalCourses ?? 0)
      : inscription.totalCourses;
    const total = inscription.totalChallenges + totalCoursesForBar;
    const chSubmitted =
      inscription.challengesSubmittedCount ??
      inscription.completedChallengesApproved;
    const coursesFinishedForBar = pdcStats
      ? pdcStats.completedCoursesCount
      : inscription.completedCoursesFinished;
    const doneForBar = chSubmitted + coursesFinishedForBar;
    if (total === 0) {
      return {
        ratio: 1,
        percentRounded: 100,
        isRegistrationComplete: true,
      };
    }
    const r = Math.min(1, Math.max(0, doneForBar / total));
    return {
      ratio: r,
      percentRounded: Math.round(r * 100),
      isRegistrationComplete: doneForBar >= total,
    };
  }, [inscription, usePdcList, pdcQuery.data]);

  const barLoading =
    isLoading || (usePdcList && pdcQuery.isLoading && pdcQuery.data === undefined);

  const showFooter =
    isValidStage &&
    isUserOutOfReality !== true &&
    !barLoading &&
    !!inscription;

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      justifyContent="center"
      width="100%"
      minHeight={{ xs: "auto", sm: "7rem" }}
    >
      <Box display="flex" justifyContent="center" width="100%">
        <Text color={Theme.colors.text.primary} fontSize="1rem" fontWeight={500}>
          Progresso do cadastro
        </Text>
      </Box>

      <Spacer height="1rem" />

      <ProgressTrack
        style={{
          minHeight: "1.75rem",
          alignItems: "center",
          justifyContent: barLoading ? "center" : "stretch",
        }}
      >
        {barLoading ? (
          <CircularProgress size={18} thickness={5} />
        ) : (
          <CadastroProgressFill $widthPercentage={ratio}>
            <Text
              color={Theme.colors.text.secondary}
              fontSize="0.75rem"
              fontWeight={600}
              component="span"
            >
              {percentRounded}%
            </Text>
          </CadastroProgressFill>
        )}
      </ProgressTrack>

      <Spacer height="1rem" />

      <Box
        display={showFooter ? "flex" : "none"}
        alignItems="flex-start"
        gap="0.35rem"
      >
        {isRegistrationComplete ? (
          <CircleCheck size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        ) : (
          <InfoIcon size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        )}
        <Text
          color={
            isRegistrationComplete
              ? Theme.colors.feedback.positive
              : Theme.colors.text.secondary
          }
          fontSize="0.75rem"
          fontWeight={400}
          lineHeight={1.35}
          component="span"
        >
          {isRegistrationComplete
            ? "Inscrição para o Reality finalizada com sucesso!"
            : "Cadastro pendente: realize as tarefas para concluir."}
        </Text>
      </Box>
    </Box>
  );
};

export default memo(MeuProgressoSection);
